/**
 * Memoized row component for a single field inside `FieldListEditor`.
 *
 * `React.memo` + stable keys (`tag + fieldId`) keep re-renders bounded
 * during drag operations on large configs (PERSONAL ~30 fields per tab,
 * total ~1200 drag subjects → see Risk R2). Without memoization every
 * field row re-renders on each drag hover, which is unusable on the
 * lowest-end machines we support.
 */
import React from 'react';
import { readCsLabel } from './labelUtils';
import { getMarcColors } from '../../styles/marcColors';
var FieldRowComponent = function (_a) {
    var field = _a.field, selected = _a.selected, onSelect = _a.onSelect, onRemove = _a.onRemove, dragHandleNode = _a.dragHandleNode, removeAriaLabel = _a.removeAriaLabel;
    var label = readCsLabel(field.label) || field.tag;
    // Reuse the MARC field-group palette shared with MarcFieldPanel /
    // MarcDataDisplay so the visual editor immediately reads as "this is
    // a 3xx" — same colour the user already sees on the record detail
    // and the rendered form.
    var colors = getMarcColors(field.tag);
    return (React.createElement("div", { "data-testid": "field-row-".concat(field.tag), role: "button", tabIndex: 0, style: {
            padding: '0.4rem 0.6rem',
            marginBottom: '0.3rem',
            // When selected, override with a stronger primary-blue outline
            // so "I'm currently editing this one" reads independently of
            // which MARC colour group the field belongs to.
            border: selected ? "2px solid #0070c0" : "1px solid ".concat(colors.border),
            borderRadius: '4px',
            backgroundColor: colors.light,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.5rem',
        }, onClick: onSelect, onKeyDown: function (e) {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect();
            }
        } },
        React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: '0.5rem' } },
            dragHandleNode,
            React.createElement("span", { style: {
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '2.5rem',
                    padding: '2px 6px',
                    fontFamily: "'SF Mono', 'Cascadia Code', 'Consolas', monospace",
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    color: '#fff',
                    backgroundColor: colors.dark,
                    borderRadius: '3px',
                    letterSpacing: '0.02em',
                } }, field.tag),
            React.createElement("span", { style: { color: colors.dark, fontWeight: 500 } }, label),
            field.repeatable && (React.createElement("span", { style: { fontSize: '0.7rem', color: colors.dark, opacity: 0.7 }, title: "repeatable" }, "\u21BB")),
            (field.indicator1Def || field.indicator2Def) && (React.createElement("span", { style: { fontSize: '0.7rem', color: colors.dark, opacity: 0.7 }, title: "indicators" }, "i"))),
        React.createElement("button", { type: "button", onClick: function (e) { e.stopPropagation(); onRemove(); }, "aria-label": removeAriaLabel, "data-testid": "field-row-".concat(field.tag, "-remove-btn"), style: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#900' } }, "\u2715")));
};
var FieldRow = React.memo(FieldRowComponent);
export default FieldRow;
